<template>
  <v-container grid-list-md>
    <v-layout wrap>
      <v-flex
        v-if="notQualified"
        warning
        pt-3
        pb-3
        pl-3
        pr-3
        mb-5
      >
        {{ $t('accounts.notQualified') }} {{ $t('investors.qualifiedTooltip') }}<br>
        <v-btn
          class="success mt-4"
          outlined
          text
          small
          @click="$router.push('/subscriptions')"
        >
          {{ $t('packs.upgrade') }}
        </v-btn>
      </v-flex>
      <v-flex xs6>
        <v-text-field
          id="name"
          v-model.trim="form.name"
          :error-messages="nameErrors"
          name="name"
          :label="$t('accounts.accName')"
          @change="$v.form.name.$touch()"
          @blur="$v.form.name.$touch()"
        />
      </v-flex>
      <v-flex xs6>
        <v-select
          v-model.trim="form.dataroom.exchange_id"
          :items="supportedExchanges"
          :item-text="'name'"
          :item-value="'id'"
          :error-messages="dataroomNameErrors"
          :label="$t('accounts.exchange')+'*'"
          :disabled="fields.id != undefined"
          name="dataroom-name"
          required
          @change="reloadQuoteAssets"
          @blur="$v.form.dataroom.exchange_id.$touch()"
        />
      </v-flex>
      <v-flex xs12>
        <v-autocomplete
          v-if="exchangeQuoteAssets && selectedExchange.quote_asset_mandatory"
          v-model.trim="form.dataroom.quote_asset"
          :items="exchangeQuoteAssets.symbols"
          :error-messages="dataroomQuoteAssetErrors"
          :label="$t('globals.quoteAsset')+'*'"
          name="dataroom-quote-asset"
          required
          @blur="$v.form.dataroom.quote_asset.$touch()"
        />
      </v-flex>

      <v-flex
        v-if="editable == true || !form.id"
        xs12
      >
        <v-text-field
          id="dataroom-apikey"
          v-model.trim="form.dataroom.apikey"
          :error-messages="dataroomApikeyErrors"
          name="dataroom-apikey"
          :label="$t('accounts.key')+'*'"
          required
          @input="$v.form.dataroom.apikey.$touch()"
          @blur="$v.form.dataroom.apikey.$touch()"
        />

        <v-text-field
          id="dataroom-secret"
          v-model.trim="form.dataroom.secret"
          :error-messages="dataroomSecretErrors"
          name="dataroom-secret"
          :label="$t('accounts.secret')+'*'"
          @input="$v.form.dataroom.secret.$touch()"
          @blur="$v.form.dataroom.secret.$touch()"
        />

        <v-list-item
          v-if="!form.id"
          class="mt-5 mb-5 pl-0"
        >
          <v-list-item-action>
            <v-switch
              v-model="importSwitch"
              color="indigo darken-2"
            />
          </v-list-item-action>
          <v-list-item-title>
            <small>{{ $t('projects.importHistory') }} </small>
            <v-tooltip
              top
              max-width="360"
            >
              <template v-slot:activator="{ on }">
                <v-icon v-on="on">
                  mdi-information-outline
                </v-icon>
              </template>
              {{ $t("projects.importHistoryHint") }}
            </v-tooltip>
          </v-list-item-title>
        </v-list-item>
        <v-menu
          v-if="importSwitch && !form.id"
          v-model="datePicker"
          :close-on-content-click="false"
          :nudge-right="120"
          top
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="formatedImportFromDate"
              :label="$t('globals.from')"
              prepend-icon="mdi-calendar"
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker 
            v-model="importFromDate" 
            :locale="$i18n.locale"
            :min="$moment().subtract(3, 'years').format('YYYY-MM-DD')"
            :max="$moment().format('YYYY-MM-DD')"
            @input="datePicker = false"
          />
        </v-menu>
      </v-flex>
      <v-flex
        v-else
        x12
      >
        <v-simple-table>
          <thead>
            <tr>
              <th class="text-left">
                {{ $t('accounts.exchange') }}
              </th>
              <th
                class="text-left"
                :style="$vuetify.breakpoint.xs ? 'max-width:140px' : ''"
              >
                {{ $t('accounts.key') }}
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr>
              <td> <b class="text-primary">{{ form.dataroom.exchange.name }}</b></td>
              <td :style="$vuetify.breakpoint.xs ? 'max-width:140px' : ''">
                {{ form.dataroom.apikey | obfuscate(5) }}
              </td>
              <td class="text-right">
                <v-btn
                  outlined
                  small
                  text
                  @click="editable = !editable"
                >
                  {{ $t('accounts.editButton') }}
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, maxLength, requiredIf } from "vuelidate/lib/validators";

import ADD_INVESTOR_ACCOUNT_MUTATION from "@/graphql/mutations/AddInvestorAccount.gql";
import UPDATE_INVESTOR_ACCOUNT_MUTATION from "@/graphql/mutations/UpdateInvestorAccount.gql";
import INVESTOR_ACCOUNTS_QUERY from "@/graphql/queries/InvestorAccounts.gql";
import EXCHANGES_QUERY from "@/graphql/queries/SupportedExchanges.gql";
import QUOTE_ASSETS_QUERY from "@/graphql/queries/ExchangeQuoteAssets.gql";

export default {
  name: "EditExchangeAccount",
  mixins: [validationMixin],
  props: {
    fields: {
      type: Object,
      required: true,
    }
  },
  data() {
    let form = {
      name: null,
      dataroom: {
        quote_asset: null,
        apikey: null,
        secret: null,
        exchange_id: null
      }
    };
    return {
      selectedExchange: null,
      notQualified: false,
      datePicker: null,
      importSwitch: true,
      importFromDate: this.$moment().subtract(7, 'days').format('YYYY-MM-DD'),
      editable: false,
      error: false,
      loading: false,
      form: form,
      initForm: form
    };
  },
  computed: {
    formatedImportFromDate() {
      return this.$moment(this.importFromDate).format('ll');
    },
    rules() {
      var form = {
        name: {
          maxLength: maxLength(20)
        },
        dataroom: {
          exchange_id: {
            required
          },
          quote_asset: {
            required
          },
          apikey: {
            required
          },
          secret: {
            required
          }
        }
      };
      if (this.form.id && !this.editable) {
        form.dataroom.apikey = {};
        form.dataroom.secret = {};
      }
      if (this.form.id || (this.selectedExchange && !this.selectedExchange.quote_asset_mandatory)) {
         form.dataroom.quote_asset = {};
      }
      return form;
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.maxLength && errors.push(this.$t("accounts.maxName"));
      return errors;
    },
    dataroomNameErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.exchange_id.$dirty) return errors;
      !this.$v.form.dataroom.exchange_id.required &&
        errors.push(this.$t("accounts.reqExchange"));
      return errors;
    },
    dataroomQuoteAssetErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.quote_asset.$dirty) return errors;
      !this.$v.form.dataroom.quote_asset.required &&
        errors.push(this.$t("globals.quoteAssetRequired"));
      return errors;
    },
    dataroomApikeyErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.apikey.$dirty) return errors;
      !this.$v.form.dataroom.apikey.required &&
        errors.push(this.$t("accounts.reqKey"));
      return errors;
    },
    dataroomSecretErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.secret.$dirty) return errors;

      this.$v.form.dataroom.secret.required !== undefined &&
        !this.$v.form.dataroom.secret.required &&
        errors.push(this.$t("accounts.reqSecret"));
      return errors;
    }
  },
  watch: {
    fields(newVal) {
      this.form = newVal.id ? this.fields : _.cloneDeep(this.initForm);
      this.$v.$reset();
      this.editable = false;
      this.error = false;
    }
  },
  mounted() {
    this.form = this.fields.id  ? this.fields : _.cloneDeep(this.initForm);
    if (this.fields.id) {
      this.form.dataroom.exchange_id = this.fields.dataroom.exchange.id
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    reloadQuoteAssets() {
      this.selectedExchange = this._.find(this.supportedExchanges, {
        id: this.form.dataroom.exchange_id
      });
    },
    async save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loading = true;
        this.error = false;
        this.notQualified = false
        
        let MUTATION = UPDATE_INVESTOR_ACCOUNT_MUTATION;

        if (!this.form.id) MUTATION = ADD_INVESTOR_ACCOUNT_MUTATION;

        let params = {}
        params.name = this.form.name

        if (!this.form.id) {
          params.dataroom = {}
          params.dataroom.exchange_id = this.form.dataroom.exchange_id
          params.dataroom.apikey = this.form.dataroom.apikey;
          params.dataroom.secret = this.form.dataroom.secret;
          if (this.selectedExchange && this.selectedExchange.quote_asset_mandatory) {
            params.dataroom.quote_asset = this.form.dataroom.quote_asset;
          }
          if (this.importSwitch) {
            params.import_from_date = this.importFromDate + ' 00:00:00'
          }
        } else {
          params.id = this.form.id
          if (this.editable) {
            params.dataroom = {
                apikey: this.form.dataroom.apikey,
                secret:this.form.dataroom.secret
            }
          }
        }
        
        await this.$apollo
          .mutate({
            mutation: MUTATION,
            variables: params,
            update: (store, { data: { addInvestorAccount } }) => {
              if (!this.form.id) {
                const data = store.readQuery({
                  query: INVESTOR_ACCOUNTS_QUERY
                });
                data.investorAccounts.push(addInvestorAccount);
                store.writeQuery({ query: INVESTOR_ACCOUNTS_QUERY, data });
              }
            }
          })
          .then(data => {
            this.loading = false;
            this.responseHandler(this.$t('globals.success'));
          })
          .catch(error => {
            if (error.graphQLErrors[0].extensions.reason == 'NotQualifiedError') {
              this.notQualified = true
            }
            this.errorHandler(error);
            this.error = error;
            this.loading = false;
          });

        if (!this.loading && !this.error) return true;
      }
      return false;
    }
  },
  apollo: {
    supportedExchanges: {
      query: EXCHANGES_QUERY
    },
    exchangeQuoteAssets: {
      query: QUOTE_ASSETS_QUERY,
      variables() {
        return {
           exchange_id: this.form.dataroom.exchange_id
        }
      },
      skip() {
        return !this.selectedExchange || !this.selectedExchange.quote_asset_mandatory
      }
    }
  } 
};
</script>

<style>
</style>
